import React, { useEffect, useState } from "react";
import { endPoints } from "../../constant/Environment";
import { addData, getAllData } from "../../Utility/API";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";
import { Button, Form } from "react-bootstrap";
import Loader from "../../components/Loader";
import { ErrorToast, SuccesToast } from "../../components/Toast/message";

/**
 * Pick which games count for this tournament. Games can come from any provider:
 * pick a provider to list its games, and the selection is kept across providers.
 */
const TournamentGamesModal = ({ open, onClose, tournament }) => {
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState("");
  const [providerGames, setProviderGames] = useState([]);
  // casino_games.Id -> game name, so chips can show games from other providers too
  const [selected, setSelected] = useState({});
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open && tournament?.id) {
      setSearch("");
      setProvider("");
      setProviderGames([]);
      loadStart();
    }
  }, [open, tournament?.id]);

  const loadStart = async () => {
    setIsLoading(true);
    const [providerResponse, current] = await Promise.all([
      getAllData(`${endPoints.api.GETPROVIDERS}`),
      getAllData(`${endPoints.api.TOURNAMENT_ADMIN}/${tournament.id}/games`),
    ]);
    setIsLoading(false);

    setProviders(providerResponse?.status === "success" ? providerResponse.data || [] : []);
    const chosen = {};
    if (current?.status === "success") {
      (current.data || []).forEach((game) => {
        chosen[game.casinoGameId] = game.gameName;
      });
    }
    setSelected(chosen);
  };

  const loadProviderGames = async (providerName) => {
    setProvider(providerName);
    setProviderGames([]);
    if (!providerName) return;
    setIsLoading(true);
    const response = await getAllData(`${endPoints.api.GAMES_BY_PROVIDER}${providerName}`);
    setIsLoading(false);
    setProviderGames(response?.status === "success" ? response.data || [] : []);
  };

  const toggleGame = (id, name) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = name;
      }
      return next;
    });
  };

  const handleSave = () => {
    const ids = Object.keys(selected).map(Number);
    setIsLoading(true);
    addData(`${endPoints.api.TOURNAMENT_ADMIN}/${tournament.id}/games`, ids)
      .then((response) => {
        setIsLoading(false);
        if (response?.data?.status === "success") {
          SuccesToast(response.data.message);
          onClose();
        } else {
          ErrorToast(response?.data?.message || "Unable to save games.");
        }
      })
      .catch((error) => {
        setIsLoading(false);
        console.error(error);
        ErrorToast("Unable to save games.");
      });
  };

  // Only switched-on games can be added; players never see inactive ones.
  const visibleGames = providerGames.filter(
    (game) => game.isactive && game.game_name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog maxWidth="sm" fullWidth open={open} onClose={onClose}>
      <DialogTitle>
        Games ({Object.keys(selected).length} selected)
      </DialogTitle>
      <DialogContent dividers>
        {isLoading && <Loader spinner={true} visible={isLoading} />}

        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "16px" }}>
          {Object.entries(selected).map(([id, name]) => (
            <Chip key={id} label={name} size="small" onDelete={() => toggleGame(Number(id), name)} />
          ))}
        </div>

        <Form.Select
          value={provider}
          onChange={(event) => loadProviderGames(event.target.value)}
          style={{ marginBottom: "12px" }}
        >
          <option value="">-- Select provider to add games --</option>
          {providers.map((item) => (
            <option key={item.provider_name} value={item.provider_name}>
              {item.provider_name}
            </option>
          ))}
        </Form.Select>

        {provider && (
          <TextField
            fullWidth
            size="small"
            label={`Search ${provider} games`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ marginBottom: "12px" }}
          />
        )}
        {provider && visibleGames.length === 0 && <div>No active games found.</div>}
        {visibleGames.map((game) => (
          <div key={game.id}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!selected[game.id]}
                  onChange={() => toggleGame(game.id, game.game_name)}
                />
              }
              label={game.game_name}
            />
          </div>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave}>Save</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TournamentGamesModal;
